import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Viewer, Worker, SpecialZoomLevel } from "@react-pdf-viewer/core";
import { zoomPlugin } from "@react-pdf-viewer/zoom";
import { fullScreenPlugin } from "@react-pdf-viewer/full-screen";
import pdfjsPackage from "pdfjs-dist/package.json";
import { renderAsync } from "docx-preview";
import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/zoom/lib/styles/index.css";
import "@react-pdf-viewer/full-screen/lib/styles/index.css";
import "./AdminApplicationDocuments.css";
import useAdminHttp from "../../../hooks/use-admin-http";
import { getAccessToken, refreshAccessToken } from "../../../utils/auth-token";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import SearchIcon from "@mui/icons-material/Search";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ZoomInOutlinedIcon from "@mui/icons-material/ZoomInOutlined";
import ZoomOutOutlinedIcon from "@mui/icons-material/ZoomOutOutlined";
import FullscreenOutlinedIcon from "@mui/icons-material/FullscreenOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import WLCustomModal from "../modals/WLCustomModal";

const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL || "http://localhost:8000/api/v1";

const pdfjsVersion = pdfjsPackage.version;

// Refetch a view URL when it expires within this window, so a preview that
// is still on screen never loads against an almost-dead SAS token.
const VIEW_URL_EXPIRY_BUFFER_MS = 60 * 1000;

// Same catalog as the applicant form (ViewingApplication), so admin slots
// line up with what applicants were asked to upload. Each slot's id is the
// document slug the backend stores (numbered slots like `salary-slip-2` are
// separate slugs there). The applicant's own documents always, whatever
// the household size; the partner's set (same documents, `-partner` slugs)
// only when the applicant rents with a partner or friend.
const EMPLOYED_SECTIONS = (withPartner) => [
  {
    title: "Identity & motivation",
    docs: [
      { id: "motivation-letter", label: "Motivation letter" },
      { id: "passport-id", label: "Passport or ID" },
      { id: "residence-permit", label: "Residence permit", optional: true },
      ...(withPartner
        ? [
            { id: "passport-id-partner", label: "Passport or ID (partner)" },
            { id: "residence-permit-partner", label: "Residence permit (partner)", optional: true },
          ]
        : []),
    ],
  },
  {
    title: "Employment & income",
    docs: [
      { id: "employment-contract", label: "Employment contract" },
      { id: "salary-slip-1", label: "Salary slip 1" },
      { id: "salary-slip-2", label: "Salary slip 2" },
      { id: "salary-slip-3", label: "Salary slip 3" },
      ...(withPartner
        ? [
            { id: "employment-contract-partner", label: "Employment contract (partner)" },
            { id: "salary-slip-partner-1", label: "Salary slip 1 (partner)" },
            { id: "salary-slip-partner-2", label: "Salary slip 2 (partner)" },
            { id: "salary-slip-partner-3", label: "Salary slip 3 (partner)" },
          ]
        : []),
    ],
  },
  {
    title: "Bank statements",
    docs: [
      { id: "bank-statement-1", label: "Bank statement 1" },
      { id: "bank-statement-2", label: "Bank statement 2" },
      { id: "bank-statement-3", label: "Bank statement 3" },
      { id: "proof-of-savings", label: "Proof of savings", optional: true },
      ...(withPartner
        ? [
            { id: "bank-statement-partner-1", label: "Bank statement 1 (partner)" },
            { id: "bank-statement-partner-2", label: "Bank statement 2 (partner)" },
            { id: "bank-statement-partner-3", label: "Bank statement 3 (partner)" },
            { id: "proof-of-savings-partner", label: "Proof of savings (partner)", optional: true },
          ]
        : []),
    ],
  },
];

// Students hand in each tenant's guarantor income documents instead of
// their own. The applicant is the 1st tenant; renting with a partner or
// friend adds a 2nd tenant with their own identity, enrolment and guarantor
// documents (the `-partner` and `-guarantor-2` slugs).
const STUDENT_SECTIONS = (withSecondTenant) => [
  {
    title: "Identity & enrolment",
    docs: [
      { id: "motivation-letter", label: "Motivation letter" },
      { id: "passport-id", label: "Passport or ID" },
      { id: "residence-permit", label: "Residence permit", optional: true },
      { id: "university-registration", label: "University registration" },
      ...(withSecondTenant
        ? [
            { id: "passport-id-partner", label: "Passport or ID (2nd tenant)" },
            { id: "residence-permit-partner", label: "Residence permit (2nd tenant)", optional: true },
            { id: "university-registration-partner", label: "University registration (2nd tenant)" },
          ]
        : []),
    ],
  },
  {
    title: "Guarantor 1st tenant",
    docs: [
      { id: "guarantor-letter", label: "Guarantor letter" },
      { id: "passport-id-guarantor-1", label: "Passport or ID (Guarantor 1st tenant)" },
      { id: "employment-contract-guarantor-1", label: "Employment contract (Guarantor 1st tenant)" },
      { id: "salary-slip-1-guarantor-1", label: "Salary slip 1 (Guarantor 1st tenant)" },
      { id: "salary-slip-2-guarantor-1", label: "Salary slip 2 (Guarantor 1st tenant)" },
      { id: "salary-slip-3-guarantor-1", label: "Salary slip 3 (Guarantor 1st tenant)" },
      { id: "bank-statement-1-guarantor-1", label: "Bank statement 1 (Guarantor 1st tenant)" },
      { id: "bank-statement-2-guarantor-1", label: "Bank statement 2 (Guarantor 1st tenant)" },
      { id: "bank-statement-3-guarantor-1", label: "Bank statement 3 (Guarantor 1st tenant)" },
      { id: "proof-of-savings-guarantor", label: "Proof of savings (Guarantor)", optional: true },
    ],
  },
  ...(withSecondTenant
    ? [
        {
          title: "Guarantor 2nd tenant",
          docs: [
            { id: "guarantor-letter-partner", label: "Guarantor letter (2nd tenant)" },
            { id: "passport-id-guarantor-2", label: "Passport or ID (Guarantor 2nd tenant)" },
            { id: "employment-contract-guarantor-2", label: "Employment contract (Guarantor 2nd tenant)" },
            { id: "salary-slip-1-guarantor-2", label: "Salary slip 1 (Guarantor 2nd tenant)" },
            { id: "salary-slip-2-guarantor-2", label: "Salary slip 2 (Guarantor 2nd tenant)" },
            { id: "salary-slip-3-guarantor-2", label: "Salary slip 3 (Guarantor 2nd tenant)" },
            { id: "bank-statement-1-guarantor-2", label: "Bank statement 1 (Guarantor 2nd tenant)" },
            { id: "bank-statement-2-guarantor-2", label: "Bank statement 2 (Guarantor 2nd tenant)" },
            { id: "bank-statement-3-guarantor-2", label: "Bank statement 3 (Guarantor 2nd tenant)" },
          ],
        },
      ]
    : []),
];

// Self-employed applicants prove their income with their business papers
// (KvK extract, two years of tax declarations and the company's bank
// statement) instead of an employment contract and salary slips. A partner
// only hands in identity documents (the `-partner` slugs).
const SELF_EMPLOYED_SECTIONS = (withPartner) => [
  {
    title: "Identity & motivation",
    docs: [
      { id: "motivation-letter", label: "Motivation letter" },
      { id: "passport-id", label: "Passport or ID" },
      { id: "residence-permit", label: "Residence permit", optional: true },
      ...(withPartner
        ? [
            { id: "passport-id-partner", label: "Passport or ID (partner)" },
            { id: "residence-permit-partner", label: "Residence permit (partner)", optional: true },
          ]
        : []),
    ],
  },
  {
    title: "Business & income",
    docs: [
      { id: "kvk-extract", label: "KvK extract" },
      { id: "tax-declaration-1", label: "Tax declaration (year 1)" },
      { id: "tax-declaration-2", label: "Tax declaration (year 2)" },
      { id: "company-bank-statement", label: "Company bank statement" },
    ],
  },
  {
    title: "Bank statements",
    docs: [
      { id: "bank-statement-1", label: "Bank statement 1" },
      { id: "bank-statement-2", label: "Bank statement 2" },
      { id: "bank-statement-3", label: "Bank statement 3" },
      { id: "proof-of-savings", label: "Proof of savings", optional: true },
    ],
  },
  {
    title: "Other",
    docs: [
      { id: "other-documents", label: "Other documents", optional: true },
    ],
  },
];

const buildDocumentSections = (profile, withPartner) => {
  if (profile === "student") return STUDENT_SECTIONS(withPartner);
  if (profile === "self-employed") return SELF_EMPLOYED_SECTIONS(withPartner);
  return EMPLOYED_SECTIONS(withPartner);
};

// The document kind behind a numbered slug: `salary-slip-2` is a
// "salary-slip", `salary-slip-partner-1` a "salary-slip-partner" and
// `salary-slip-2-guarantor-1` a "salary-slip-guarantor".
const docKind = (slug) => String(slug).replace(/-\d+(?=-|$)/g, "");

// Documents of the partner or 2nd tenant end in `-partner`; a 2nd tenant's
// guarantor documents end in `-guarantor-2`.
const isPartnerSlug = (slug) => /-(partner|guarantor-2)$/.test(slug);

// Same limits as the applicant form, so an admin cannot store a file the
// applicant could not have uploaded themselves.
const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;
const UPLOAD_ACCEPT =
  "application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/*";
const isAllowedUploadType = (file) =>
  file.type === "application/pdf" ||
  file.type ===
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
  file.type.startsWith("image/");

const formatFileSize = (bytes) => {
  if (bytes == null) return null;
  return bytes >= 1024 * 1024
    ? `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} KB`;
};

const formatDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (isNaN(date)) return null;
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(date.getDate())}-${pad(date.getMonth() + 1)}-${date.getFullYear()}`;
};

const StatusIcon = ({ review }) => {
  if (review === "approved") {
    return (
      <span className="wlad-doc-status wlad-doc-status--approved">
        <CheckIcon />
      </span>
    );
  }
  if (review === "flagged") {
    return (
      <span className="wlad-doc-status wlad-doc-status--flagged">
        <WarningAmberOutlinedIcon />
      </span>
    );
  }
  return <span className="wlad-doc-status" />;
};

const SKELETON_WIDTHS = [46, 88, 68, 47, 90, 69];

// The backend may serve DOCX files with a generic content type, so also
// fall back to the file extension.
const isDocx = (contentType, filename) =>
  (contentType || "").includes("wordprocessingml") ||
  /\.docx$/i.test(filename || "");

// Renders a DOCX inline by downloading it and letting docx-preview lay the
// pages out in the container, so the file never leaves our own storage.
const DocxViewer = ({ url, filename }) => {
  const containerRef = useRef(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    let cancelled = false;
    const container = containerRef.current;
    setStatus("loading");
    const load = async () => {
      try {
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`Download failed (${response.status})`);
        }
        const blob = await response.blob();
        if (cancelled) return;
        await renderAsync(blob, container);
        if (!cancelled) setStatus("ready");
      } catch (error) {
        console.error("Could not render DOCX preview", error);
        if (!cancelled) setStatus("error");
      }
    };
    load();
    return () => {
      cancelled = true;
      if (container) container.innerHTML = "";
    };
  }, [url]);

  return (
    <div className="wlad-doc-docx">
      {status === "loading" && (
        <div className="wlad-doc-docx-note">Loading {filename}...</div>
      )}
      {status === "error" && (
        <div className="wlad-doc-docx-note">
          Could not render this document.{" "}
          <a href={url} target="_blank" rel="noreferrer">
            Open {filename}
          </a>
        </div>
      )}
      <div className="wlad-doc-docx-body" ref={containerRef} />
    </div>
  );
};

// Suggested questions per document type, shown above the input bar.
const ASK_SUGGESTIONS = {
  "motivation-letter": [
    "Why do they want this home?",
    "How long do they plan to stay?",
    "Who will be living in the home?",
  ],
  "passport-id": [
    "When does the passport expire?",
    "What nationality is the holder?",
    "Does the name match the application?",
  ],
  "passport-id-partner": [
    "When does the passport expire?",
    "What nationality is the holder?",
    "Is this a different person than the applicant?",
  ],
  "residence-permit": [
    "When does the permit expire?",
    "What type of permit is this?",
  ],
  "residence-permit-partner": [
    "When does the permit expire?",
    "What type of permit is this?",
  ],
  "university-registration": [
    "Which university or school is this?",
    "Which programme and year?",
    "Until when is the enrolment valid?",
  ],
  "university-registration-partner": [
    "Which university or school is this?",
    "Which programme and year?",
    "Until when is the enrolment valid?",
  ],
  "guarantor-letter": [
    "Who is the guarantor?",
    "What do they guarantee, and for how long?",
    "Is the letter signed?",
  ],
  "guarantor-letter-partner": [
    "Who is the guarantor?",
    "What do they guarantee, and for how long?",
    "Is the letter signed?",
  ],
  "passport-id-guarantor": [
    "When does the passport expire?",
    "Does the name match the guarantor letter?",
  ],
  "employment-contract-guarantor": [
    "What kind of employment contract?",
    "What is the gross salary?",
    "Does the name match the guarantor letter?",
  ],
  "salary-slip-guarantor": [
    "What is the net monthly income?",
    "Who is the employer?",
    "Does the income match the contract?",
  ],
  "bank-statement-guarantor": [
    "What is the closing balance?",
    "Are there any unusual transactions?",
  ],
  "proof-of-savings-guarantor": [
    "How much savings are shown?",
    "Can they cover the deposit?",
  ],
  "employment-contract": [
    "What kind of employment contract?",
    "What is the gross salary?",
    "Is there a probation period?",
  ],
  "employment-contract-partner": [
    "What kind of employment contract?",
    "What is the gross salary?",
    "Does the employer match the application?",
  ],
  "salary-slip": [
    "What is the net monthly income?",
    "Who is the employer?",
    "Does the income match the contract?",
  ],
  "salary-slip-partner": [
    "What is the net monthly income?",
    "Who is the employer?",
    "Does the income match the application?",
  ],
  "bank-statement": [
    "What is the closing balance?",
    "Are they paying rent on time now?",
    "Are there any unusual transactions?",
  ],
  "bank-statement-partner": [
    "What is the closing balance?",
    "Are they paying rent on time now?",
    "Are there any unusual transactions?",
  ],
  "proof-of-savings": [
    "How much savings are shown?",
    "Can they cover the deposit?",
  ],
  "proof-of-savings-partner": [
    "How much savings are shown?",
    "Can they cover the deposit?",
  ],
};

const DEFAULT_ASK_SUGGESTIONS = [
  "Summarize this document",
  "Is there anything inconsistent?",
];

const AskDocumentChat = ({
  applicationId,
  uploadId,
  docSlug,
  docLabel,
  applicantName,
  thread,
  onAppend,
}) => {
  const [input, setInput] = useState("");
  // Question awaiting an answer, with the upload it belongs to, so a reply
  // that arrives after switching documents still lands in the right thread.
  const [pending, setPending] = useState(null);
  const threadRef = useRef(null);
  const inputRef = useRef(null);
  const { isLoading, error, sendRequest } = useAdminHttp();

  const ask = (question, { echo = true } = {}) => {
    const trimmed = question.trim();
    if (!trimmed || isLoading) return;
    if (echo) onAppend(uploadId, { role: "user", text: trimmed });
    setPending({ uploadId, text: trimmed });
    setInput("");
    sendRequest(
      {
        url: `${BACKEND_URL}/welocate/applications/${applicationId}/document-uploads/${uploadId}/ask`,
        method: "POST",
        body: { question: trimmed },
      },
      (data) => {
        onAppend(uploadId, {
          role: "assistant",
          text: data.answer,
          sources: data.sources,
        });
        setPending(null);
      }
    );
  };

  useEffect(() => {
    if (error && pending) {
      onAppend(pending.uploadId, {
        role: "error",
        text: error,
        question: pending.text,
      });
      setPending(null);
    }
  }, [error, pending, onAppend]);

  const waiting = isLoading && pending?.uploadId === uploadId;

  useEffect(() => {
    const el = threadRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [thread.length, waiting]);

  useEffect(() => {
    inputRef.current?.focus();
  }, [uploadId]);

  const suggestions =
    ASK_SUGGESTIONS[docKind(docSlug)] || DEFAULT_ASK_SUGGESTIONS;

  const handleSubmit = (e) => {
    e.preventDefault();
    ask(input);
  };

  return (
    <div className="wlad-ask">
      <div className="wlad-ask-thread" ref={threadRef}>
        {thread.length === 0 && !waiting ? (
          <div className="wlad-ask-empty">
            <h3 className="wlad-ask-empty-title">
              Ask anything about {applicantName ? `${applicantName}'s` : "the"}{" "}
              {docLabel.toLowerCase()}
            </h3>
            <p className="wlad-ask-empty-sub">
              Answers are grounded in the uploaded file and cite their source.
            </p>
          </div>
        ) : (
          <>
            {thread.map((message, i) =>
              message.role === "error" ? (
                <div className="wlad-ask-msg wlad-ask-msg--error" key={i}>
                  {message.text}{" "}
                  <button
                    type="button"
                    className="wlad-doc-retry"
                    onClick={() => ask(message.question, { echo: false })}
                  >
                    Try again
                  </button>
                </div>
              ) : (
                <div
                  className={`wlad-ask-msg wlad-ask-msg--${message.role}`}
                  key={i}
                >
                  {message.text}
                  {message.sources?.length > 0 && (
                    <div className="wlad-ask-sources">
                      {message.sources.map((source, j) => (
                        <span className="wlad-ask-source" key={j}>
                          {typeof source === "string"
                            ? source
                            : source.page != null
                            ? `Page ${source.page}`
                            : source.label || source.filename}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )
            )}
            {waiting && (
              <div className="wlad-ask-msg wlad-ask-msg--assistant wlad-ask-typing">
                <span />
                <span />
                <span />
              </div>
            )}
          </>
        )}
      </div>

      <div className="wlad-ask-suggestions">
        {suggestions.map((suggestion) => (
          <button
            type="button"
            className="wlad-ask-suggestion"
            key={suggestion}
            onClick={() => ask(suggestion)}
          >
            {suggestion}
          </button>
        ))}
      </div>

      <form className="wlad-ask-bar" onSubmit={handleSubmit}>
        <input
          ref={inputRef}
          className="wlad-ask-input"
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="e.g. Is there anything inconsistent in this document?"
        />
        <button
          type="submit"
          className="wlad-ask-send"
          disabled={!input.trim() || isLoading}
        >
          Ask
        </button>
      </form>
    </div>
  );
};

// A failed BLOCKING rule reads as a hard failure; a failed FLAG rule is a
// point of attention rather than a rejection, and a failed NOTE rule is
// merely informational, so each severity gets its own styling.
const prescanKind = (record) =>
  record.status === "PASSED"
    ? "passed"
    : record.validation_rule?.severity === "FLAG"
    ? "flagged"
    : record.validation_rule?.severity === "NOTE"
    ? "note"
    : "failed";

const PRESCAN_BADGE_LABELS = {
  passed: "Passed",
  flagged: "Flagged",
  note: "Note",
  failed: "Failed",
};

const PrescanRuleIcon = ({ kind }) => {
  if (kind === "passed") return <CheckIcon />;
  if (kind === "flagged") return <WarningAmberOutlinedIcon />;
  if (kind === "note") return <InfoOutlinedIcon />;
  return <CloseIcon />;
};

// Count chips shown on the AI prescan tab and in the document list, one
// per kind. "Passed" is included so a clean document reads as green rather
// than showing nothing at all.
const PRESCAN_COUNT_CHIPS = [
  { kind: "failed", label: "blocking", Icon: ErrorOutlineOutlinedIcon },
  { kind: "flagged", label: "flagged", Icon: WarningAmberOutlinedIcon },
  { kind: "note", label: "note", Icon: InfoOutlinedIcon },
  { kind: "passed", label: "passed", Icon: CheckIcon },
];

const prescanCounts = (upload) =>
  (upload?.validation_records || []).reduce((counts, record) => {
    const kind = prescanKind(record);
    counts[kind] = (counts[kind] || 0) + 1;
    return counts;
  }, {});

const DocPrescanCounts = ({ upload }) => {
  if (upload.validation_in_progress || upload.is_na) return null;
  const counts = prescanCounts(upload);
  if ((upload.validation_records || []).length === 0) return null;
  return (
    <span className="wlad-doc-counts" title="AI prescan results">
      <InsightsOutlinedIcon className="wlad-doc-counts-icon" />
      {PRESCAN_COUNT_CHIPS.map(({ kind, label, Icon }) => {
        const count = counts[kind] || 0;
        if (count === 0) return null;
        return (
          <span
            className={`wlad-tab-issue wlad-tab-issue--${kind}`}
            key={kind}
            title={`${count} ${label}`}
          >
            <Icon />
            {count}
          </span>
        );
      })}
    </span>
  );
};

const AiPrescanPanel = ({ upload, docLabel, meta }) => {
  const records = upload.validation_records || [];
  const passedCount = records.filter((r) => r.status === "PASSED").length;
  const allPassed = passedCount === records.length;

  return (
    <div className="wlad-scan">
      <div className="wlad-scan-head">
        <h2 className="wlad-viewer-title">{docLabel}</h2>
        {meta && <div className="wlad-viewer-meta">{meta}</div>}
      </div>

      {upload.validation_in_progress ? (
        <div className="wlad-scan-empty">
          <span className="wlad-doc-badge">
            <span className="wlad-doc-badge-spinner" />
            Validating
          </span>
          <p>
            The AI prescan is still running for this document. Results will
            appear here once it finishes.
          </p>
        </div>
      ) : records.length === 0 ? (
        <div className="wlad-scan-empty">
          <p>No validation rules have run for this document.</p>
        </div>
      ) : (
        <>
          <div className="wlad-scan-section">
            <InsightsOutlinedIcon />
            <span className="wlad-scan-section-title">
              Validation rules for this document type
            </span>
            <span
              className={`wlad-scan-count${
                allPassed ? "" : " wlad-scan-count--issues"
              }`}
            >
              {passedCount}/{records.length} passed
            </span>
          </div>

          <div className="wlad-scan-rules">
            {records.map((record) => {
              const kind = prescanKind(record);
              return (
                <div
                  className={`wlad-scan-rule wlad-scan-rule--${kind}`}
                  key={record.id}
                >
                  <span
                    className={`wlad-scan-rule-icon wlad-scan-rule-icon--${kind}`}
                  >
                    <PrescanRuleIcon kind={kind} />
                  </span>
                  <div className="wlad-scan-rule-body">
                    <div className="wlad-scan-rule-title">
                      <span className="wlad-scan-rule-name">
                        {record.validation_rule?.name || "Validation rule"}
                      </span>
                      <span
                        className={`wlad-scan-rule-badge wlad-scan-rule-badge--${kind}`}
                      >
                        {PRESCAN_BADGE_LABELS[kind]}
                      </span>
                    </div>
                    {record.validation_rule?.requirement && (
                      <p className="wlad-scan-rule-req">
                        {record.validation_rule.requirement}
                      </p>
                    )}
                    {kind !== "passed" && record.details && (
                      <p className="wlad-scan-rule-details">
                        {record.details}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

const AdminApplicationDocuments = ({
  applicationId,
  uploads,
  reviews,
  onReview,
  onRefetch,
  applicantName,
  profile,
  withPartner,
}) => {
  // Show the partner slots when the application says there is a partner,
  // and never hide partner documents that were actually uploaded (the
  // answer may have been changed to "no" later).
  const showPartnerDocs = useMemo(
    () =>
      Boolean(withPartner) ||
      uploads.some((u) => isPartnerSlug(u.document_slug)),
    [withPartner, uploads]
  );

  const sections = useMemo(
    () =>
      buildDocumentSections(profile, showPartnerDocs).map((section) => ({
        ...section,
        docs: section.docs.map((doc) => ({
          ...doc,
          upload: uploads.find((u) => u.document_slug === doc.id) || null,
        })),
      })),
    [uploads, profile, showPartnerDocs]
  );

  const uploadedDocs = useMemo(
    () => sections.flatMap((s) => s.docs).filter((d) => d.upload),
    [sections]
  );

  const [selectedId, setSelectedId] = useState(null);
  const selected =
    uploadedDocs.find((d) => d.id === selectedId) || uploadedDocs[0] || null;
  const selectedIndex = selected ? uploadedDocs.indexOf(selected) : -1;

  // The one document group unfolded in the list. It follows the selected
  // document (sidebar click or previous/next paging) and can be switched by
  // hand.
  const [openSectionTitle, setOpenSectionTitle] = useState(null);
  const selectedDocId = selected?.id;
  useEffect(() => {
    const section = sections.find((s) =>
      s.docs.some((doc) => doc.id === selectedDocId)
    );
    if (section) setOpenSectionTitle(section.title);
  }, [sections, selectedDocId]);

  const [activeTab, setActiveTab] = useState("document");

  // Chat threads keyed by upload id, kept here so switching documents or
  // tabs does not throw a conversation away.
  const [chats, setChats] = useState({});
  const appendChatMessage = useCallback((id, message) => {
    setChats((prev) => ({ ...prev, [id]: [...(prev[id] || []), message] }));
  }, []);

  // Short-lived SAS view URLs, keyed by upload id. Fetched when a document
  // is selected and reused while paging until they are close to expiry.
  const [previews, setPreviews] = useState({});
  const [previewRetry, setPreviewRetry] = useState(0);
  const previewsRef = useRef(previews);
  previewsRef.current = previews;
  const {
    isLoading: previewLoading,
    error: previewError,
    sendRequest: requestViewUrl,
  } = useAdminHttp();

  const zoomPluginInstance = zoomPlugin();
  const { ZoomIn, ZoomOut, CurrentScale } = zoomPluginInstance;
  const fullScreenPluginInstance = fullScreenPlugin();
  const { EnterFullScreen } = fullScreenPluginInstance;

  const uploadId = selected?.upload?.id ?? null;
  const selectedIsNa = Boolean(selected?.upload?.is_na);

  // Issue counts for the selected document, shown on the AI prescan tab so
  // reviewers see at a glance whether the scan found anything.
  const prescanIssueCounts = useMemo(
    () => prescanCounts(selected?.upload),
    [selected]
  );
  const preview = uploadId != null ? previews[uploadId] : null;

  useEffect(() => {
    // "Not applicable" entries have no file behind them, so never request
    // a view URL for one.
    if (uploadId == null || selectedIsNa) return;
    const cached = previewsRef.current[uploadId];
    if (
      cached &&
      (!cached.expiresOn ||
        Date.parse(cached.expiresOn) - Date.now() > VIEW_URL_EXPIRY_BUFFER_MS)
    ) {
      return;
    }
    requestViewUrl(
      {
        url: `${BACKEND_URL}/welocate/applications/${applicationId}/document-uploads/${uploadId}/view-url`,
      },
      (data) =>
        setPreviews((prev) => ({
          ...prev,
          [uploadId]: {
            url: data.url,
            contentType: data.content_type,
            expiresOn: data.expires_on,
          },
        }))
    );
  }, [uploadId, selectedIsNa, applicationId, requestViewUrl, previewRetry]);

  // Downloads go through a blob so the browser saves the file under its
  // original name instead of navigating to the SAS URL.
  const [downloading, setDownloading] = useState(false);
  const downloadDocument = async () => {
    if (!preview || downloading) return;
    setDownloading(true);
    try {
      const response = await fetch(preview.url);
      if (!response.ok) {
        throw new Error(`Download failed (${response.status})`);
      }
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = selected.upload.filename || selected.label;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (error) {
      console.error("Could not download the document", error);
      window.open(preview.url, "_blank", "noopener");
    } finally {
      setDownloading(false);
    }
  };

  // Downloads the zip of every uploaded file. useAdminHttp always parses
  // JSON, so this goes through fetch directly with the same auth scheme:
  // bearer token, cookie credentials, and one refresh-and-retry on a 401.
  const [zipState, setZipState] = useState("idle");
  const downloadAllDocuments = async () => {
    if (zipState === "loading") return;
    setZipState("loading");
    try {
      const fetchZip = () => {
        const token = getAccessToken();
        return fetch(
          `${BACKEND_URL}/welocate/applications/${applicationId}/document-uploads/zip`,
          {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
            credentials: "include",
          }
        );
      };
      let response = await fetchZip();
      if (response.status === 401 && (await refreshAccessToken())) {
        response = await fetchZip();
      }
      if (!response.ok) {
        throw new Error(`Download failed (${response.status})`);
      }
      const blob = await response.blob();
      const disposition = response.headers.get("Content-Disposition") || "";
      const filenameMatch = disposition.match(/filename="?([^";]+)"?/i);
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = filenameMatch
        ? filenameMatch[1]
        : `application-${applicationId}-documents.zip`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(objectUrl);
      setZipState("idle");
    } catch (error) {
      console.error("Could not download the documents zip", error);
      setZipState("error");
    }
  };

  // "Not applicable" entries have no file behind them, so the zip button
  // only appears when at least one real file exists.
  const downloadableCount = uploadedDocs.filter((d) => !d.upload.is_na).length;

  // Admin upload for a slot the applicant left empty. Posts to the same
  // document-uploads endpoint as the applicant form (file plus slug), then
  // asks the parent to refetch the application so the new upload shows up
  // with its prescan.
  const uploadInputRef = useRef(null);
  // Slot whose file picker is open; only becomes the upload target once a
  // file is actually chosen, so closing the picker leaves the row idle.
  const pickerDocRef = useRef(null);
  // The slot being uploaded into. Stays set from the file pick until the
  // refetched application actually contains the new upload, so the row
  // never flips back to "Not uploaded" between the POST and the refetch.
  const [uploadTarget, setUploadTarget] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const {
    isLoading: uploading,
    error: uploadRequestError,
    sendRequest: sendUploadRequest,
  } = useAdminHttp();
  const uploadPending = uploadTarget != null;

  const startUpload = (doc) => {
    if (uploadPending) return;
    pickerDocRef.current = doc;
    setUploadError(null);
    // Reset first so picking the same file twice still fires onChange.
    if (uploadInputRef.current) {
      uploadInputRef.current.value = "";
      uploadInputRef.current.click();
    }
  };

  const handleUploadFile = (file) => {
    const doc = pickerDocRef.current;
    pickerDocRef.current = null;
    if (!file || !doc) return;
    if (!isAllowedUploadType(file)) {
      setUploadError({
        docId: doc.id,
        message: "Only PDF, DOCX and image files can be uploaded.",
      });
      return;
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      setUploadError({
        docId: doc.id,
        message: "This file is larger than 10 MB.",
      });
      return;
    }
    setUploadTarget(doc);
    const body = new FormData();
    body.append("file", file);
    body.append("document_slug", doc.id);
    // Admin uploads skip the applicant flow, so ask the backend to start the
    // AI prescan right away.
    body.append("auto_validate", "true");
    sendUploadRequest(
      {
        url: `${BACKEND_URL}/welocate/applications/${applicationId}/document-uploads`,
        method: "POST",
        body,
        isFileUpload: true,
      },
      () => {
        if (onRefetch) onRefetch();
      }
    );
  };

  // The refetch delivered the new upload: hand the slot over to the normal
  // row and open the document in the viewer only now, so the viewer never
  // falls back to the first document in between.
  const uploadTargetUploaded = Boolean(
    uploadTarget &&
      sections
        .flatMap((s) => s.docs)
        .find((d) => d.id === uploadTarget.id)?.upload
  );
  useEffect(() => {
    if (!uploadTargetUploaded) return;
    setSelectedId(uploadTarget.id);
    setUploadTarget(null);
  }, [uploadTargetUploaded, uploadTarget]);

  // Deleting an upload (file or "not applicable" entry) asks for
  // confirmation first, then refetches so the slot shows as missing again.
  const [deleteTarget, setDeleteTarget] = useState(null);
  const {
    isLoading: deleting,
    error: deleteError,
    sendRequest: sendDeleteRequest,
  } = useAdminHttp();

  const confirmDelete = () => {
    if (!deleteTarget || deleting) return;
    const targetUploadId = deleteTarget.upload.id;
    sendDeleteRequest(
      {
        url: `${BACKEND_URL}/welocate/applications/${applicationId}/document-uploads/${targetUploadId}`,
        method: "DELETE",
      },
      () => {
        setDeleteTarget(null);
        // Drop the cached view URL so a later upload in this slot never
        // shows the old file.
        setPreviews((prev) => {
          const { [targetUploadId]: removed, ...rest } = prev;
          return rest;
        });
        if (onRefetch) onRefetch();
      }
    );
  };

  useEffect(() => {
    if (uploadRequestError && uploadTarget) {
      setUploadError({ docId: uploadTarget.id, message: uploadRequestError });
      setUploadTarget(null);
    }
  }, [uploadRequestError, uploadTarget]);

  const reviewedCount = uploadedDocs.filter(
    (d) => reviews[d.upload.id]
  ).length;
  const progress =
    uploadedDocs.length > 0 ? (reviewedCount / uploadedDocs.length) * 100 : 0;

  const goTo = (index) => {
    const doc = uploadedDocs[index];
    if (doc) setSelectedId(doc.id);
  };

  const selectedMeta = selected
    ? selectedIsNa
      ? [
          "Marked as not applicable",
          formatDate(selected.upload.created_on) &&
            `on ${formatDate(selected.upload.created_on)}`,
        ]
          .filter(Boolean)
          .join(" ")
      : [
          selected.upload.filename,
          formatFileSize(selected.upload.size ?? selected.upload.file_size),
          formatDate(selected.upload.created_on) &&
            `uploaded ${formatDate(selected.upload.created_on)}`,
        ]
          .filter(Boolean)
          .join(" · ")
    : null;

  return (
    <div className="wlad-docs">
      <aside className="wlad-docs-list">
        <div className="wlad-docs-list-header">
          <span className="wlad-card-title wlad-docs-list-title">
            <span className="wlad-card-dot" />
            Documents
          </span>
          <span className="wlad-docs-reviewed">
            {reviewedCount}/{uploadedDocs.length} reviewed
          </span>
        </div>
        <div className="wlad-docs-progress">
          <div
            className="wlad-docs-progress-fill"
            style={{ width: `${progress}%` }}
          />
        </div>

        {downloadableCount > 0 && (
          <>
            <button
              type="button"
              className="wlad-docs-download"
              onClick={downloadAllDocuments}
              disabled={zipState === "loading"}
            >
              <FileDownloadOutlinedIcon />
              {zipState === "loading"
                ? "Preparing zip..."
                : "Download all documents"}
            </button>
            {zipState === "error" && (
              <div className="wlad-docs-download-error">
                Could not download the zip.{" "}
                <button
                  type="button"
                  className="wlad-doc-retry"
                  onClick={downloadAllDocuments}
                >
                  Try again
                </button>
              </div>
            )}
          </>
        )}

        {sections.map((section) => {
          const visibleDocs = section.docs.filter(
            (doc) => doc.upload || !doc.optional
          );
          if (visibleDocs.length === 0) return null;
          const isOpen = section.title === openSectionTitle;
          const uploadedCount = visibleDocs.filter((doc) => doc.upload).length;
          return (
            <div
              className={`wlad-docs-section${
                isOpen ? " wlad-docs-section--open" : ""
              }`}
              key={section.title}
            >
              <button
                type="button"
                className="wlad-docs-section-head"
                aria-expanded={isOpen}
                onClick={() =>
                  setOpenSectionTitle(isOpen ? null : section.title)
                }
              >
                <span className="wlad-docs-section-title">{section.title}</span>
                <span
                  className={`wlad-docs-section-count${
                    uploadedCount === visibleDocs.length
                      ? " wlad-docs-section-count--done"
                      : ""
                  }`}
                >
                  {uploadedCount}/{visibleDocs.length}
                </span>
                <ChevronRightIcon className="wlad-docs-section-chevron" />
              </button>
              {isOpen && visibleDocs.map((doc) =>
                doc.upload ? (
                  <button
                    type="button"
                    key={doc.id}
                    className={`wlad-doc-row${
                      selected?.id === doc.id ? " wlad-doc-row--active" : ""
                    }`}
                    onClick={() => setSelectedId(doc.id)}
                  >
                    <StatusIcon review={reviews[doc.upload.id]} />
                    <span className="wlad-doc-name">{doc.label}</span>
                    <DocPrescanCounts upload={doc.upload} />
                    {doc.upload.validation_in_progress && (
                      <span className="wlad-doc-badge">
                        <span className="wlad-doc-badge-spinner" />
                        Validating
                      </span>
                    )}
                    <span className="wlad-doc-size">
                      {doc.upload.is_na
                        ? "N/A"
                        : formatFileSize(
                            doc.upload.size ?? doc.upload.file_size
                          ) || ""}
                    </span>
                  </button>
                ) : (
                  <div key={doc.id} className="wlad-doc-missing">
                    <div className="wlad-doc-row wlad-doc-row--missing">
                      <StatusIcon />
                      <span className="wlad-doc-name">{doc.label}</span>
                      {uploadTarget?.id === doc.id ? (
                        <span className="wlad-doc-badge">
                          <span className="wlad-doc-badge-spinner" />
                          {uploading ? "Uploading" : "Validating"}
                        </span>
                      ) : (
                        <>
                          <span className="wlad-doc-size">Not uploaded</span>
                          <button
                            type="button"
                            className="wlad-doc-upload"
                            onClick={() => startUpload(doc)}
                            disabled={uploadPending}
                            title={`Upload ${doc.label.toLowerCase()}`}
                          >
                            <FileUploadOutlinedIcon />
                            Upload
                          </button>
                        </>
                      )}
                    </div>
                    {uploadError?.docId === doc.id && (
                      <div className="wlad-doc-upload-error">
                        {uploadError.message}{" "}
                        <button
                          type="button"
                          className="wlad-doc-retry"
                          onClick={() => startUpload(doc)}
                        >
                          Try again
                        </button>
                      </div>
                    )}
                  </div>
                )
              )}
            </div>
          );
        })}

        <input
          ref={uploadInputRef}
          type="file"
          accept={UPLOAD_ACCEPT}
          hidden
          onChange={(event) => {
            handleUploadFile(event.target.files[0]);
            event.target.value = "";
          }}
        />
      </aside>

      <section className="wlad-viewer">
        {selected ? (
          <>
            <div className="wlad-viewer-top">
              <div className="wlad-viewer-tabs">
                <button
                  type="button"
                  className={`wlad-viewer-tab${
                    activeTab === "document" ? " wlad-viewer-tab--active" : ""
                  }`}
                  onClick={() => setActiveTab("document")}
                >
                  <VisibilityOutlinedIcon /> Document
                </button>
                <button
                  type="button"
                  className={`wlad-viewer-tab${
                    activeTab === "ask" ? " wlad-viewer-tab--active" : ""
                  }`}
                  onClick={() => setActiveTab("ask")}
                  disabled={selectedIsNa}
                >
                  <SearchIcon /> Ask the document
                </button>
                <button
                  type="button"
                  className={`wlad-viewer-tab${
                    activeTab === "prescan" ? " wlad-viewer-tab--active" : ""
                  }`}
                  onClick={() => setActiveTab("prescan")}
                  disabled={selectedIsNa}
                >
                  <InsightsOutlinedIcon /> AI prescan
                  {!selectedIsNa &&
                    !selected.upload.validation_in_progress &&
                    PRESCAN_COUNT_CHIPS.map(({ kind, label, Icon }) => {
                      const count = prescanIssueCounts[kind] || 0;
                      if (count === 0) return null;
                      return (
                        <span
                          className={`wlad-tab-issue wlad-tab-issue--${kind}`}
                          key={kind}
                          title={`${count} ${label}`}
                        >
                          <Icon />
                          {count}
                        </span>
                      );
                    })}
                </button>
              </div>
              <div className="wlad-viewer-pager">
                <button
                  type="button"
                  className="wlad-viewer-pager-btn"
                  onClick={() => goTo(selectedIndex - 1)}
                  disabled={selectedIndex <= 0}
                >
                  <ChevronLeftIcon />
                </button>
                <span className="wlad-viewer-pager-count">
                  {selectedIndex + 1} / {uploadedDocs.length}
                </span>
                <button
                  type="button"
                  className="wlad-viewer-pager-btn"
                  onClick={() => goTo(selectedIndex + 1)}
                  disabled={selectedIndex >= uploadedDocs.length - 1}
                >
                  <ChevronRightIcon />
                </button>
              </div>
            </div>

            {activeTab === "ask" && !selectedIsNa ? (
              <AskDocumentChat
                applicationId={applicationId}
                uploadId={selected.upload.id}
                docSlug={selected.id}
                docLabel={selected.label}
                applicantName={applicantName}
                thread={chats[selected.upload.id] || []}
                onAppend={appendChatMessage}
              />
            ) : activeTab === "prescan" && !selectedIsNa ? (
              <AiPrescanPanel
                upload={selected.upload}
                docLabel={selected.label}
                meta={selectedMeta}
              />
            ) : (
              <>
            <div className="wlad-viewer-head">
              <div>
                <div className="wlad-viewer-title-row">
                  <h2 className="wlad-viewer-title">{selected.label}</h2>
                  {selected.upload.validation_in_progress && (
                    <span className="wlad-doc-badge">
                      <span className="wlad-doc-badge-spinner" />
                      Validating
                    </span>
                  )}
                </div>
                {selectedMeta && (
                  <div className="wlad-viewer-meta">{selectedMeta}</div>
                )}
              </div>
              <div className="wlad-viewer-actions">
                {!selectedIsNa && (
                  <button
                    type="button"
                    className="wlad-btn"
                    onClick={downloadDocument}
                    disabled={!preview || downloading}
                  >
                    <FileDownloadOutlinedIcon />
                    {downloading ? "Downloading..." : "Download"}
                  </button>
                )}
                <button
                  type="button"
                  className={`wlad-btn${
                    reviews[selected.upload.id] === "flagged"
                      ? " wlad-btn--flag-active"
                      : ""
                  }`}
                  onClick={() => onReview(selected.upload.id, "flagged")}
                >
                  <WarningAmberOutlinedIcon /> Flag
                </button>
                <button
                  type="button"
                  className={`wlad-btn${
                    reviews[selected.upload.id] === "approved"
                      ? " wlad-btn--approve-active"
                      : ""
                  }`}
                  onClick={() => onReview(selected.upload.id, "approved")}
                >
                  <CheckIcon /> Approve
                </button>
                <button
                  type="button"
                  className="wlad-btn wlad-btn--danger"
                  onClick={() => setDeleteTarget(selected)}
                  title={`Delete ${selected.label.toLowerCase()}`}
                >
                  <DeleteOutlineIcon /> Delete
                </button>
              </div>
            </div>

            <div className="wlad-viewer-stage">
              {selectedIsNa ? (
                <div className="wlad-doc-page">
                  <div className="wlad-doc-page-head">
                    <span>{selected.label}</span>
                    <span>
                      {[applicantName, formatDate(selected.upload.created_on)]
                        .filter(Boolean)
                        .join(" · ")}
                    </span>
                  </div>
                  <div className="wlad-doc-page-rule" />
                  <p className="wlad-doc-page-note">
                    {applicantName || "The applicant"} indicated that this
                    document does not apply to their situation.
                  </p>
                  {selected.upload.is_na_reason ? (
                    <blockquote className="wlad-doc-na-reason">
                      {selected.upload.is_na_reason}
                    </blockquote>
                  ) : (
                    <p className="wlad-doc-na-reason wlad-doc-na-reason--empty">
                      No reason was given.
                    </p>
                  )}
                </div>
              ) : preview ? (
                (preview.contentType || "").startsWith("image/") ? (
                  <img
                    className="wlad-doc-image"
                    src={preview.url}
                    alt={selected.label}
                  />
                ) : (preview.contentType || "").includes("pdf") ? (
                  <div className="wlad-doc-frame">
                    <div className="wlad-doc-toolbar">
                      <ZoomOut>
                        {(props) => (
                          <button
                            type="button"
                            className="wlad-doc-tool"
                            onClick={props.onClick}
                            aria-label="Zoom out"
                          >
                            <ZoomOutOutlinedIcon />
                          </button>
                        )}
                      </ZoomOut>
                      <CurrentScale>
                        {(props) => (
                          <span className="wlad-doc-zoom-level">
                            {Math.round(props.scale * 100)}%
                          </span>
                        )}
                      </CurrentScale>
                      <ZoomIn>
                        {(props) => (
                          <button
                            type="button"
                            className="wlad-doc-tool"
                            onClick={props.onClick}
                            aria-label="Zoom in"
                          >
                            <ZoomInOutlinedIcon />
                          </button>
                        )}
                      </ZoomIn>
                      <EnterFullScreen>
                        {(props) => (
                          <button
                            type="button"
                            className="wlad-doc-tool wlad-doc-tool--right"
                            onClick={props.onClick}
                            aria-label="Full screen"
                          >
                            <FullscreenOutlinedIcon />
                          </button>
                        )}
                      </EnterFullScreen>
                    </div>
                    <div className="wlad-doc-canvas">
                      <Worker
                        workerUrl={`https://unpkg.com/pdfjs-dist@${pdfjsVersion}/build/pdf.worker.min.js`}
                      >
                        <Viewer
                          fileUrl={preview.url}
                          defaultScale={SpecialZoomLevel.PageWidth}
                          plugins={[zoomPluginInstance, fullScreenPluginInstance]}
                        />
                      </Worker>
                    </div>
                  </div>
                ) : isDocx(preview.contentType, selected.upload.filename) ? (
                  <DocxViewer
                    key={selected.upload.id}
                    url={preview.url}
                    filename={selected.upload.filename}
                  />
                ) : (
                  <div className="wlad-doc-page">
                    <div className="wlad-doc-page-head">
                      <span>{selected.label}</span>
                      <span>
                        {[applicantName, formatDate(selected.upload.created_on)]
                          .filter(Boolean)
                          .join(" · ")}
                      </span>
                    </div>
                    <div className="wlad-doc-page-rule" />
                    <p className="wlad-doc-page-note">
                      No inline preview for this file type.{" "}
                      <a href={preview.url} target="_blank" rel="noreferrer">
                        Open {selected.upload.filename}
                      </a>
                    </p>
                  </div>
                )
              ) : (
                <div className="wlad-doc-page">
                  <div className="wlad-doc-page-head">
                    <span>{selected.label}</span>
                    <span>
                      {[applicantName, formatDate(selected.upload.created_on)]
                        .filter(Boolean)
                        .join(" · ")}
                    </span>
                  </div>
                  <div className="wlad-doc-page-rule" />
                  <p className="wlad-doc-page-note">
                    {previewLoading
                      ? `Loading ${selected.upload.filename}...`
                      : previewError
                      ? "Could not load the preview. "
                      : `Preview of ${selected.upload.filename} is not available.`}
                    {!previewLoading && previewError && (
                      <button
                        type="button"
                        className="wlad-doc-retry"
                        onClick={() => setPreviewRetry((n) => n + 1)}
                      >
                        Try again
                      </button>
                    )}
                  </p>
                  {SKELETON_WIDTHS.map((width, i) => (
                    <div
                      key={i}
                      className="wlad-doc-skeleton"
                      style={{ width: `${width}%` }}
                    />
                  ))}
                </div>
              )}
            </div>
              </>
            )}
          </>
        ) : (
          <div className="wlad-viewer-empty">No documents uploaded yet.</div>
        )}
      </section>

      {deleteTarget && (
        <WLCustomModal
          title="Delete document"
          visible={true}
          setVisible={(open) => {
            if (!open && !deleting) setDeleteTarget(null);
          }}
          btnText={deleting ? "Deleting..." : "Delete document"}
          btnDisabled={deleting}
          btnOnClick={confirmDelete}
          secondaryBtnText="Cancel"
        >
          <p className="wlad-doc-delete-text">
            Are you sure you want to delete{" "}
            <strong>{deleteTarget.label}</strong>
            {deleteTarget.upload.is_na
              ? ", marked as not applicable"
              : deleteTarget.upload.filename
              ? ` (${deleteTarget.upload.filename})`
              : ""}
            ? The slot will show as not uploaded again. This cannot be
            undone.
          </p>
          {deleteError && (
            <div className="wlad-doc-delete-error">{deleteError}</div>
          )}
        </WLCustomModal>
      )}
    </div>
  );
};

export default AdminApplicationDocuments;
